import { useState, useEffect, useMemo } from 'react';
import { Table, Text, Center, Button, Loader, Tooltip } from '@mantine/core';
import type { DataLibraryCoachPostSessionRow, DataLibrarySurveyQuestion, ColumnAverages } from '../../../types';
import { flattenGroups, type GroupNode } from '../../../utils/groupDataByField';
import type { SortConfig, ColumnConfig } from '../../../hooks/useColumnConfig';
import { SortableTableHeader, NonSortableColumnHeader } from '../../common/SortableTableHeader';
import { ColumnAveragesRow } from '../../common/ConfigurableTable';
import { CollapsibleGroupHeader } from '../components/CollapsibleGroupHeader';
import { renderCoachPostSessionCell } from './CoachPostSessionCellRenderers';
import classes from '../DataLibraryDrawer.module.css';
import { ResizableHeaderCell } from '../components/ResizableHeaderCell';
import { useDataLibraryColumnWidths } from '../useDataLibraryTableLayout';
import { ColumnWidthCell } from '../components/ColumnWidthCell';
import { DataLibraryEmptyState } from '../components/DataLibraryEmptyState';

interface CoachPostSessionTableProps {
  data: DataLibraryCoachPostSessionRow[];
  groupedData: GroupNode<DataLibraryCoachPostSessionRow>[] | null;
  columns: ColumnConfig[];
  questions: DataLibrarySurveyQuestion[];
  sortConfig: SortConfig | null;
  onSort: (key: string) => void;
  loading: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  emptyMessage: string;
  page: number;
  pageSize: number;
  totalCount: number;
  /** MDF-41: server averages over all matching rows */
  averages: ColumnAverages;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  hidePagination?: boolean;
}

// Columns whose cell has its own Tooltip get no title. Add a new one here, or a cut cell shows two boxes.
const TOOLTIP_COLUMNS = ['attendance', 'absence_reasons', 'coach_notes', 'engagement', 'concern_flags'];

function getColStyle(colId: string): React.CSSProperties {
  const wide = ['attendance', 'engagement', 'concern_flags'].includes(colId);
  const medium = ['absence_reasons', 'coach_notes'].includes(colId);
  return {
    minWidth: wide ? 400 : medium ? 300 : 200,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  };
}

const PAGE_SIZES = [25, 50, 100, 250];

export function CoachPostSessionTable({
  data,
  groupedData,
  columns,
  questions,
  sortConfig,
  onSort,
  loading,
  hasActiveFilters,
  onClearFilters,
  emptyMessage,
  page,
  pageSize,
  totalCount,
  averages,
  totalPages,
  onPageChange,
  onPageSizeChange,
  hidePagination,
}: CoachPostSessionTableProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const layout = useDataLibraryColumnWidths();

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Reset collapsed state when groups change
  const groupKeys = groupedData ? groupedData.map(g => g.key).join('\0') : '';
  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [groupKeys]);

  // MED-888: build a quick lookup from `q_<question_id>` → full question text so
  // dynamic question columns can render the short label with the full text on hover.
  const questionTextById = useMemo(() => {
    const map = new Map<string, string>();
    for (const q of questions) {
      map.set(`q_${q.question_id}`, q.question_text);
    }
    return map;
  }, [questions]);

  if (loading) {
    return (
      <Center h={200}>
        <Loader size="sm" />
      </Center>
    );
  }

  const averagesRow = (
    <ColumnAveragesRow
      columns={columns}
      averages={averages}
      rows={data}
      totalCount={totalCount}
    />
  );

  const hasData = data.length > 0 || (groupedData && groupedData.length > 0);

  if (!hasData) {
    return <DataLibraryEmptyState message={emptyMessage} hasActiveFilters={hasActiveFilters} onClearFilters={onClearFilters} />;
  }

  const startRecord = (page - 1) * pageSize + 1;
  const endRecord = Math.min(page * pageSize, totalCount);

  const tableHeader = (
    <Table.Thead>
      <Table.Tr>
        {columns.map(col => {
          const tooltipText = questionTextById.get(col.id);
          const headerNode = col.sortable ? (
            <SortableTableHeader
              label={col.label}
              sortKey={col.id}
              currentSort={sortConfig}
              onSort={onSort}
            />
          ) : (
            <NonSortableColumnHeader label={col.label} />
          );
          return (
            <ResizableHeaderCell key={col.id} column={col} style={getColStyle(col.id)} layout={layout}>
              {tooltipText && tooltipText !== col.label ? (
                <Tooltip label={tooltipText} withArrow multiline w={320} position="top-start">
                  <span style={{ display: 'inline-block' }}>{headerNode}</span>
                </Tooltip>
              ) : (
                headerNode
              )}
            </ResizableHeaderCell>
          );
        })}
      </Table.Tr>
    </Table.Thead>
  );

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <Table striped highlightOnHover withTableBorder>
          {tableHeader}
          <Table.Tbody>
            {averagesRow}
            {groupedData
              ? flattenGroups(groupedData, collapsedGroups).map(item =>
                  'group' in item ? (
                    <CollapsibleGroupHeader
                      key={`group-${item.group.key}`}
                      label={item.group.label}
                      count={item.group.rows.length}
                      depth={item.group.depth}
                      colSpan={columns.length}
                      columns={columns}
                      rows={item.group.rows}
                      isCollapsed={collapsedGroups.has(item.group.key)}
                      onToggle={() => toggleGroup(item.group.key)}
                    />
                  ) : (
                    <Table.Tr key={item.row.session_id} className={classes.tableRow}>
                      {columns.map(col => (
                        <ColumnWidthCell key={col.id} style={getColStyle(col.id)} width={layout.columnWidths[col.id]} hasTooltip={TOOLTIP_COLUMNS.includes(col.id)}>
                          {renderCoachPostSessionCell(item.row, col.id, questions)}
                        </ColumnWidthCell>
                      ))}
                    </Table.Tr>
                  ),
                )
              : data.map(row => (
                  <Table.Tr key={row.session_id} className={classes.tableRow}>
                    {columns.map(col => (
                      <ColumnWidthCell key={col.id} style={getColStyle(col.id)} width={layout.columnWidths[col.id]} hasTooltip={TOOLTIP_COLUMNS.includes(col.id)}>
                        {renderCoachPostSessionCell(row, col.id, questions)}
                      </ColumnWidthCell>
                    ))}
                  </Table.Tr>
                ))}
          </Table.Tbody>
        </Table>
      </div>

      {!hidePagination && totalCount > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
          <Text size="sm" c="teal">
            Showing {startRecord}-{endRecord} of {totalCount} sessions
          </Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Text size="sm" c="dimmed">Rows per page:</Text>
            <select
              value={pageSize}
              onChange={e => onPageSizeChange(Number(e.target.value))}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #E0DED9' }}
            >
              {PAGE_SIZES.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 4 }}>
              <Button size="xs" variant="subtle" disabled={page <= 1} onClick={() => onPageChange(1)}>{'«'}</Button>
              <Button size="xs" variant="subtle" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>{'‹'}</Button>
              <Button size="xs" variant="filled" color="teal">{page}</Button>
              <Button size="xs" variant="subtle" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>{'›'}</Button>
              <Button size="xs" variant="subtle" disabled={page >= totalPages} onClick={() => onPageChange(totalPages)}>{'»'}</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
